import { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { Users, Store, Package, ShoppingCart, Settings, Info, Handshake, MessageSquare } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { getStats } from '../../lib/firebaseService';

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/dashboard');
      return;
    }
    loadStats();
  }, [user, navigate]);

  const loadStats = async () => {
    try {
      const data = await getStats();
      setStats(data);
    } catch (error) {
      console.error('Stats yüklənə bilmədi:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Yüklənir...</p>
      </div>
    );
  }

  const statCards = [
    { icon: Users, label: 'İstifadəçilər', value: stats?.total_users || 0, color: 'text-gray-700', bg: 'bg-gray-50', link: '/admin/users' },
    { icon: Store, label: 'Mağazalar', value: stats?.total_stores || 0, color: 'text-green-600', bg: 'bg-green-50', link: '/admin/stores' },
    { icon: Package, label: 'Məhsullar', value: stats?.total_products || 0, color: 'text-purple-600', bg: 'bg-purple-50' },
    { icon: ShoppingCart, label: 'Sifarişlər', value: stats?.total_orders || 0, color: 'text-orange-600', bg: 'bg-orange-50' },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <header className="bg-white border-b border-border">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-bold text-primary">Admin Panel</h1>
          </div>
          <div className="flex gap-3">
            <Link to="/dashboard">
              <Button variant="outline">İstifadəçi Paneli</Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold mb-2">Xoş gəldiniz, {user?.full_name}</h2>
          <p className="text-muted-foreground">Sistemin ümumi statistikası</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards.map((stat) => {
            const Icon = stat.icon;
            const content = (
              <div className="bg-white rounded-xl border border-border p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-12 h-12 ${stat.bg} rounded-lg flex items-center justify-center`}>
                    <Icon className={`w-6 h-6 ${stat.color}`} />
                  </div>
                </div>
                <p className="text-3xl font-bold mb-1">{stat.value}</p>
                <p className="text-sm text-muted-foreground">{stat.label}</p>
              </div>
            );
            
            return stat.link ? (
              <Link key={stat.label} to={stat.link}>
                {content}
              </Link>
            ) : (
              <div key={stat.label}>{content}</div>
            );
          })}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Link to="/admin/users">
            <div className="bg-white rounded-xl border border-border p-6 hover:shadow-lg transition-shadow cursor-pointer">
              <div className="flex items-center gap-3 mb-3">
                <Users className="w-6 h-6 text-gray-700" />
                <h3 className="text-xl font-semibold">İstifadəçiləri İdarə Et</h3>
              </div>
              <p className="text-muted-foreground">Bütün qeydiyyatlı istifadəçiləri görüntüləyin</p>
            </div>
          </Link>
          
          <Link to="/admin/stores">
            <div className="bg-white rounded-xl border border-border p-6 hover:shadow-lg transition-shadow cursor-pointer">
              <div className="flex items-center gap-3 mb-3">
                <Store className="w-6 h-6 text-green-600" />
                <h3 className="text-xl font-semibold">Mağazalar & Limitlər</h3>
              </div>
              <p className="text-muted-foreground">Mağaza limitlərini idarə edin</p>
            </div>
          </Link>

          <Link to="/admin/plans">
            <div className="bg-white rounded-xl border border-border p-6 hover:shadow-lg transition-shadow cursor-pointer">
              <div className="flex items-center gap-3 mb-3">
                <Settings className="w-6 h-6 text-purple-600" />
                <h3 className="text-xl font-semibold">Abunəlik Planları</h3>
              </div>
              <p className="text-muted-foreground">Planları əlavə edin və idarə edin</p>
            </div>
          </Link>

          <Link to="/admin/about">
            <div className="bg-white rounded-xl border border-border p-6 hover:shadow-lg transition-shadow cursor-pointer">
              <div className="flex items-center gap-3 mb-3">
                <Settings className="w-6 h-6 text-gray-700" />
                <h3 className="text-xl font-semibold">Haqqımızda</h3>
              </div>
              <p className="text-muted-foreground">Sayt haqqında məlumatları idarə edin</p>
            </div>
          </Link>

          <Link to="/admin/partners">
            <div className="bg-white rounded-xl border border-border p-6 hover:shadow-lg transition-shadow cursor-pointer">
              <div className="flex items-center gap-3 mb-3">
                <Handshake className="w-6 h-6 text-amber-600" />
                <h3 className="text-xl font-semibold">Tərəfdaşlarımız</h3>
              </div>
              <p className="text-muted-foreground">Tərəfdaş şirkətləri idarə edin</p>
            </div>
          </Link>

          <Link to="/admin/reviews">
            <div className="bg-white rounded-xl border border-border p-6 hover:shadow-lg transition-shadow cursor-pointer">
              <div className="flex items-center gap-3 mb-3">
                <MessageSquare className="w-6 h-6 text-pink-600" />
                <h3 className="text-xl font-semibold">Müştəri Rəyləri</h3>
              </div>
              <p className="text-muted-foreground">Rəyləri təsdiqləyin və idarə edin</p>
            </div>
          </Link>
        </div>
      </main>
    </div>
  );
}
